/**
 * WebSocket connection manager.
 *
 * One singleton (`wsManager`) per browser tab. Owns:
 *   • The WebSocket instance (when in real mode; null in mock mode).
 *   • Reconnect timer with full-jitter exponential backoff.
 *   • Heartbeat timer (20s ping; server expected to echo).
 *   • An incoming-event buffer flushed once per `requestAnimationFrame`,
 *     so a burst of 200 msgs/sec turns into one React render per ~16 ms
 *     rather than 200 (mitigation for risk #6 — 60 fps budget).
 *   • A Zustand store mirror (`useWsStore`) for React components.
 *   • A direct-subscription channel for low-latency consumers (charts,
 *     blotters) that can't tolerate the React-render hop.
 *
 * In mock mode (no URL configured), the manager doesn't open a socket; the
 * mock publisher in `@/lib/mock/ws` feeds events through `_inject()` and
 * everything else (buffer, RAF flush, store update) works identically.
 */

'use client';

import { create } from 'zustand';

import type { WsAllEvents } from '@/types/events';

/* ============================================================================
 * Connection state
 * ============================================================================ */

export const CONNECTION_STATES = ['idle', 'connecting', 'open', 'reconnecting', 'closed', 'error'] as const;
export type ConnectionState = (typeof CONNECTION_STATES)[number];

/* ============================================================================
 * Diagnostics snapshot
 * ============================================================================ */

export interface WsDiagnostics {
  state: ConnectionState;
  lastEventAt: number | null;
  /** Wall-clock latency of the last server-acknowledged ping (ms), or null. */
  lastPingRttMs: number | null;
  /** Cumulative count of messages dropped (parse failures, buffer overruns). */
  droppedMessages: number;
  /** Cumulative count of reconnect attempts since boot. */
  reconnectAttempts: number;
  /** Cumulative count of received messages since boot. */
  receivedMessages: number;
}

/* ============================================================================
 * Zustand store — what React components subscribe to.
 *
 * The store is updated ONCE per RAF flush (not per event) so a 200 msgs/sec
 * burst causes ~60 store updates/sec at most.
 * ============================================================================ */

const RECENT_EVENT_CAP = 100;

interface WsStoreState {
  /** Most recent events (capped at RECENT_EVENT_CAP). Newest last. */
  events: WsAllEvents[];
  /** Per-`type` running counts. */
  counts: Partial<Record<WsAllEvents['type'], number>>;
  diagnostics: WsDiagnostics;
}

interface WsStoreActions {
  _applyBatch: (batch: WsAllEvents[]) => void;
  _setConnectionState: (state: ConnectionState) => void;
  _setPingRtt: (ms: number | null) => void;
  _bumpReconnectAttempts: () => void;
  _bumpDropped: (n?: number) => void;
  /** Test-only — wipes counters and events. Throws outside test mode. */
  _resetForTesting: () => void;
}

const emptyDiagnostics: WsDiagnostics = {
  state: 'idle',
  lastEventAt: null,
  lastPingRttMs: null,
  droppedMessages: 0,
  reconnectAttempts: 0,
  receivedMessages: 0,
};

export const useWsStore = create<WsStoreState & WsStoreActions>()((set, get) => ({
  events: [],
  counts: {},
  diagnostics: emptyDiagnostics,

  _applyBatch: (batch) => {
    if (batch.length === 0) {
      return;
    }
    const prev = get();
    const events = [...prev.events, ...batch];
    if (events.length > RECENT_EVENT_CAP) {
      events.splice(0, events.length - RECENT_EVENT_CAP);
    }
    const counts: Partial<Record<WsAllEvents['type'], number>> = { ...prev.counts };
    for (const e of batch) {
      counts[e.type] = (counts[e.type] ?? 0) + 1;
    }
    set({
      events,
      counts,
      diagnostics: {
        ...prev.diagnostics,
        lastEventAt: Date.now(),
        receivedMessages: prev.diagnostics.receivedMessages + batch.length,
      },
    });
  },

  _setConnectionState: (state) =>
    set((s) => ({ diagnostics: { ...s.diagnostics, state } })),

  _setPingRtt: (lastPingRttMs) =>
    set((s) => ({ diagnostics: { ...s.diagnostics, lastPingRttMs } })),

  _bumpReconnectAttempts: () =>
    set((s) => ({
      diagnostics: { ...s.diagnostics, reconnectAttempts: s.diagnostics.reconnectAttempts + 1 },
    })),

  _bumpDropped: (n = 1) =>
    set((s) => ({
      diagnostics: { ...s.diagnostics, droppedMessages: s.diagnostics.droppedMessages + n },
    })),

  _resetForTesting: () => {
    const isTest = typeof process !== 'undefined' && process.env.NODE_ENV === 'test';
    if (!isTest) {
      throw new Error('_resetForTesting is only available in test mode');
    }
    set({ events: [], counts: {}, diagnostics: emptyDiagnostics });
  },
}));

/* ============================================================================
 * Backoff (full-jitter), capped at 30s
 * ============================================================================ */

const fullJitter = (attempt: number, baseMs: number, capMs: number): number => {
  const expo = Math.min(capMs, baseMs * 2 ** attempt);
  return Math.floor(Math.random() * expo);
};

/* ============================================================================
 * The manager
 * ============================================================================ */

export interface WsManagerConfig {
  /** WS URL. When `null`, the manager runs in mock mode — `_inject` only. */
  url: string | null;
  /** Bearer token to send as the first message on connect (matches the wire
   *  shape backend will adopt in Tier C3). */
  bearerToken: string | null;
  /** Heartbeat interval (ms). Default 20_000. */
  heartbeatMs: number;
  /** Reconnect backoff base (ms). Default 500. */
  reconnectBaseMs: number;
  /** Reconnect backoff cap (ms). Default 30_000. */
  reconnectCapMs: number;
}

type Subscriber = (event: WsAllEvents) => void;

class WsManager {
  private config: WsManagerConfig = {
    url: null,
    bearerToken: null,
    heartbeatMs: 20_000,
    reconnectBaseMs: 500,
    reconnectCapMs: 30_000,
  };

  private socket: WebSocket | null = null;
  private buffer: WsAllEvents[] = [];
  private rafHandle: number | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private lastPingSentAt: number | null = null;
  private reconnectAttempts = 0;
  private subscribers = new Set<Subscriber>();
  private intentionalClose = false;

  configure(next: Partial<WsManagerConfig>): void {
    this.config = { ...this.config, ...next };
  }

  /**
   * Start the manager.
   *   • Real mode (url !== null): opens the socket, schedules reconnects.
   *   • Mock mode (url === null): stays in 'idle' state; `_inject()` does the work.
   */
  start(): void {
    if (this.config.url === null) {
      this.setState('idle');
      return;
    }
    this.intentionalClose = false;
    this.connect();
  }

  /**
   * Stop the manager. Cancels timers, closes the socket cleanly, flushes any
   * buffered events.
   */
  stop(): void {
    this.intentionalClose = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
    if (this.socket !== null) {
      try {
        this.socket.close(1000, 'manager.stop()');
      } catch {
        /* ignored */
      }
      this.socket = null;
    }
    this.setState('closed');
  }

  /**
   * Subscribe to the raw event stream. Use this only for low-latency
   * consumers (chart series patcher, blotter row inserter); React tiles
   * should `useWsStore` and let the RAF batching pace them.
   */
  subscribe(handler: Subscriber): () => void {
    this.subscribers.add(handler);
    return () => {
      this.subscribers.delete(handler);
    };
  }

  /**
   * Inject an event as if it had arrived from the wire. Used by:
   *   • the mock WS publisher (`@/lib/mock/ws`)
   *   • Storybook for component-state tests
   *   • Playwright fixtures that need deterministic event delivery
   */
  inject(event: WsAllEvents): void {
    this.buffer.push(event);
    this.scheduleFlush();
  }

  /** Flush synchronously — test-only escape hatch. */
  _flushSync(): void {
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
    this.flush();
  }

  getDiagnostics(): WsDiagnostics {
    return useWsStore.getState().diagnostics;
  }

  /* ─── Internals ───────────────────────────────────────────────────────── */

  private connect(): void {
    const url = this.config.url;
    if (url === null) {
      return;
    }
    // A previous socket may still be live (stop()→start() re-sync, or a
    // reconnect timer racing a manual restart). Close it BEFORE replacing
    // this.socket, or its 'message' listener keeps injecting — every event
    // would be delivered twice (double-counted fills/pnl on the console).
    if (this.socket !== null) {
      try {
        this.socket.close(1000, 'superseded by reconnect');
      } catch {
        /* ignored */
      }
      this.socket = null;
    }
    this.setState(this.reconnectAttempts === 0 ? 'connecting' : 'reconnecting');
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      this.setState('error');
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    // Every handler below guards on socket identity: once this.socket is
    // reassigned (stop()→start(), supersede above), events from the OLD
    // socket must be inert. Without the guard, the old socket's async
    // 'close' fires AFTER start() reset intentionalClose — killing the new
    // socket's heartbeat and scheduling a ghost reconnect (orphaned third
    // socket + duplicate event delivery).

    socket.addEventListener('open', () => {
      if (this.socket !== socket) {
        return;
      }
      this.reconnectAttempts = 0;
      this.setState('open');

      // Send the bearer token as the first message so the server can auth.
      if (this.config.bearerToken !== null && socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(JSON.stringify({ type: 'auth', token: this.config.bearerToken }));
        } catch {
          /* socket closed mid-send — reconnect logic will pick up */
        }
      }
      this.startHeartbeat();
    });

    socket.addEventListener('message', (ev: MessageEvent) => {
      if (this.socket !== socket) {
        return;
      }
      // pong / auth-ack messages are tracked separately; everything else hits the buffer.
      let event: WsAllEvents | { type: 'pong' } | null = null;
      try {
        event = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as WsAllEvents | { type: 'pong' };
      } catch {
        useWsStore.getState()._bumpDropped();
        return;
      }
      if (event === null) {
        useWsStore.getState()._bumpDropped();
        return;
      }
      if (event.type === 'pong') {
        if (this.lastPingSentAt !== null) {
          useWsStore.getState()._setPingRtt(Date.now() - this.lastPingSentAt);
          this.lastPingSentAt = null;
        }
        return;
      }
      this.inject(event as WsAllEvents);
    });

    socket.addEventListener('close', () => {
      if (this.socket !== socket) {
        return;
      }
      this.stopHeartbeat();
      if (this.intentionalClose) {
        this.setState('closed');
        return;
      }
      this.scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      if (this.socket !== socket) {
        return;
      }
      this.setState('error');
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) {
      return;
    }
    this.setState('reconnecting');
    const delay = fullJitter(this.reconnectAttempts, this.config.reconnectBaseMs, this.config.reconnectCapMs);
    this.reconnectAttempts += 1;
    useWsStore.getState()._bumpReconnectAttempts();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private startHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      return;
    }
    this.heartbeatTimer = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.lastPingSentAt = Date.now();
        try {
          this.socket.send(JSON.stringify({ type: 'ping', at: this.lastPingSentAt }));
        } catch {
          /* will be picked up on next reconnect */
        }
      }
    }, this.config.heartbeatMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Schedule a RAF-paced flush. Multiple calls within the same frame
   * coalesce — this is the 60fps guarantee.
   */
  private scheduleFlush(): void {
    if (this.rafHandle !== null) {
      return;
    }
    // requestAnimationFrame may be undefined in non-browser test envs.
    if (typeof requestAnimationFrame === 'undefined') {
      // Fallback: macrotask-based microflush. Tests typically call _flushSync.
      this.rafHandle = setTimeout(() => {
        this.rafHandle = null;
        this.flush();
      }, 16) as unknown as number;
      return;
    }
    this.rafHandle = requestAnimationFrame(() => {
      this.rafHandle = null;
      this.flush();
    });
  }

  private flush(): void {
    if (this.buffer.length === 0) {
      return;
    }
    const batch = this.buffer;
    this.buffer = [];

    // Direct subscribers — low-latency consumers (charts / blotters).
    if (this.subscribers.size > 0) {
      for (const event of batch) {
        for (const sub of this.subscribers) {
          try {
            sub(event);
          } catch {
            /* an individual subscriber must not break the fan-out */
          }
        }
      }
    }

    // Single store update for the whole batch — React tiles re-render once.
    useWsStore.getState()._applyBatch(batch);
  }

  private setState(state: ConnectionState): void {
    useWsStore.getState()._setConnectionState(state);
  }
}

/* ============================================================================
 * Singleton + helpers
 * ============================================================================ */

export const wsManager = new WsManager();

/** React hook — current connection state. */
export const useWsConnectionState = (): ConnectionState =>
  useWsStore((s) => s.diagnostics.state);

/** React hook — full diagnostics snapshot. */
export const useWsDiagnostics = (): WsDiagnostics => useWsStore((s) => s.diagnostics);

/** React hook — recent events (capped at RECENT_EVENT_CAP). */
export const useWsEvents = (): WsAllEvents[] => useWsStore((s) => s.events);

/** React hook — per-type counts. */
export const useWsCounts = (): Partial<Record<WsAllEvents['type'], number>> =>
  useWsStore((s) => s.counts);
